import React from 'react'
import { Row, Column, H2, Typography, useScreenClass } from '@vp/swan'
import { useStyles } from '@vp/ubik-context'
import { ProductTile, type ExtendedMtsLocalizationResponse } from '@vp/ep-tiles'
import { stylesheet as productTileStylesheet } from '@vp/ep-tiles/product-tile.css'
import { ProductRecommendationItem } from '../../types/Recommendations'
import { useAppContext } from '../../state/AppContext'
import { trackClickEvent } from '../../utils/tracking/tracking'
import {
  getEffectiveTilesPerRow,
  MOBILE_COL_SPAN
} from '../../utils/recommendations/productRecommendationsBuilder'

export interface ProductRecommendationsGridProps {
  title?: string
  subtitle?: string
  products: ProductRecommendationItem[]
  colSpan: 6 | 4 | 3 | 2
  headerColSpan: 5 | 7
  pageSection: string
  calloutsLocalization: ExtendedMtsLocalizationResponse
  tilesPerRow?: 4 | 6
  setRowRef?: (rowIndex: number) => (el: HTMLDivElement | null) => void
  onTileClick?: (product: ProductRecommendationItem, globalIndex: number) => void
}

export const ProductRecommendationsGrid = ({
  title,
  subtitle,
  products,
  colSpan,
  headerColSpan,
  pageSection,
  calloutsLocalization,
  tilesPerRow = 4,
  setRowRef,
  onTileClick
}: ProductRecommendationsGridProps) => {
  useStyles(productTileStylesheet)
  const { merchandisingCategoryId } = useAppContext()
  const screenClass = useScreenClass()
  const isMobile = screenClass === 'xs'

  const effectiveTilesPerRow = getEffectiveTilesPerRow(tilesPerRow, screenClass)
  const effectiveColSpan: 6 | 4 | 3 | 2 = isMobile ? MOBILE_COL_SPAN : colSpan

  if (products.length === 0) {
    return null
  }

  const handleTileClick = (product: ProductRecommendationItem, productIndex: number) => {
    trackClickEvent({
      categoryId: merchandisingCategoryId,
      pageZone: pageSection,
      ctaValue: product.name,
      destinationUrl: product.productUrl
    })

    onTileClick?.(product, productIndex)
  }

  const rows: ProductRecommendationItem[][] = []
  for (let i = 0; i < products.length; i += effectiveTilesPerRow) {
    rows.push(products.slice(i, i + effectiveTilesPerRow))
  }

  return (
    <>
      {(title || subtitle) && (
        <Row mb={4}>
          <Column span={headerColSpan}>
            {title && <H2 fontSize="x2large">{title}</H2>}
            {subtitle && <Typography fontSize="standard">{subtitle}</Typography>}
          </Column>
        </Row>
      )}
      {rows.map((rowProducts, rowIndex) => (
        <Row key={rowIndex} ref={setRowRef?.(rowIndex)} mb={6}>
          {rowProducts.map((product, tileIndexInRow) => {
            const globalIndex = rowIndex * effectiveTilesPerRow + tileIndexInRow
            return (
              <Column span={effectiveColSpan} spanXs={6} key={product.mpvId}>
                <ProductTile
                  data-testid="recommendations-grid-tile"
                  data-mpv-id={product.mpvId}
                  product={{
                    productKey: product.productKey,
                    mpvId: product.mpvId,
                    productVersion: product.productVersion,
                    name: product.name,
                    description: product.description,
                    productUrl: product.productUrl,
                    image: {
                      src: product.image?.src ?? '',
                      alt: product.image?.alt ?? product.name
                    },
                    inStock: !product.isOutOfStock,
                    selectedOptions: product.selectedOptions
                  }}
                  {...(product.callout !== undefined && { callout: product.callout })}
                  ratingSummary={product.ratingSummary}
                  calloutsLocalization={calloutsLocalization}
                  favourite={{ pageZone: pageSection, productPosition: globalIndex }}
                  callbacks={{ onTileClick: () => handleTileClick(product, globalIndex) }}
                />
              </Column>
            )
          })}
        </Row>
      ))}
    </>
  )
}

export default ProductRecommendationsGrid
